import React, { FC, useEffect, useState } from 'react';
import { TextField, Typography, Stack, Dialog, Select, MenuItem, InputLabel, FormControl, DialogContent, DialogActions, DialogTitle, IconButton, Chip } from '@mui/material';
import '../../scss/AddressForm.scss';
import intl from 'components/i18n/ReactIntlWrapper';
import ErrorSummary from 'components/common/ErrorSummary';
import { PermissionsGate } from 'components/PermissionsGate';
import { HiOutlinePlus, HiOutlineSave } from 'react-icons/hi';
import { Country, CreatePersonaCommand, Invoice, InvoicingEligibility, ListOptionDTO, Persona, UpdatePersonaCommand } from 'axiosApi/models';
import TypeAhead from 'components/common/TypeAhead';
import Api from 'axiosApi/api';
import { useApi } from 'api/ApiProvider';
import { Button } from 'reactstrap';
import { MdClose } from 'react-icons/md';
import { z, ZodType } from "zod";
import { ENTITY_TYPE } from 'common/constants';
import Spinner from 'components/Spinner';

type PersonaBillingModalProps = {
    editScopes?: string[];
    deleteScopes?: string[];
    remainingPersonaTypes?: ListOptionDTO[];
    editPersona?: Persona | null;
    entityId?: number;
    idEntityType?: number;
    onClose?: () => void;
    onSave?: () => void;
    billToDisplay?: boolean;
}

const personaUpdateSchema = (billToDisplay: boolean): ZodType<UpdatePersonaCommand> => z.object({
    id: z.number().min(1, intl.get('validate.errors.isRequired')),
    name: z.string().min(1, intl.get('validate.errors.isRequired')),
    email: z.string().optional().nullable(),
    taxID: z.string().min(1, intl.get('validate.errors.isRequired')),
    idCountry: z.number().min(1, intl.get('validate.errors.isRequired')),
    personaToIds: billToDisplay ? z.array(z.number()).min(1, intl.get('validate.errors.isRequired')) : z.array(z.number()).optional()
});

const personaCreateSchema = (billToDisplay: boolean): ZodType<CreatePersonaCommand> => z.object({
    name: z.string().min(1, intl.get('validate.errors.isRequired')),
    email: z.string().optional().nullable(),
    taxID: z.string().min(1, intl.get('validate.errors.isRequired')),
    idCountry: z.number().min(1, intl.get('validate.errors.isRequired')),
    idPersonaType: z.number().min(1, intl.get('validate.errors.isRequired')),
    idEntity: z.number().optional(),
    idEntityType: z.number().optional(),
    personaToIds: billToDisplay ? z.array(z.number()).min(1, intl.get('validate.errors.isRequired')) : z.array(z.number()).optional(),
});

const PersonaBillingModal: FC<PersonaBillingModalProps> = ({
    editScopes = [],
    deleteScopes = [],
    remainingPersonaTypes = [],
    editPersona = null,
    entityId = 0,
    idEntityType = 0,
    onClose = () => {},
    onSave = () => {},
    billToDisplay = true,
}) => {
    const [mode, setMode] = useState<'edit' | 'add'>('add');
    const { invoiceApi, addressApi }: Api = useApi()
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    // Form state
    const [personaTo, setPersonaTo] = useState<ListOptionDTO[]>([]);
    const [formData, setFormData] = useState<any>({
        id: 0,
        name: '',
        taxID: '',
        idCountry: 0,
        personaToIds: [],
    });

    const [personaTypes, setPersonaTypes] = useState<ListOptionDTO[]>([]);
    const [country, setCountry] = useState<Country>();

    // Handle form input changes
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
        const { name, value } = e.target;
        if (name) {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    // Handle corporate entities selection
    const handleCorporateEntitiesChange = (event: any, newValues: ListOptionDTO[]) => {
        setPersonaTo(newValues);
    };

    // Handle persona type selection
    const handlePersonaTypeChange = (e: any) => {
        const { value } = e.target;
        setFormData(prev => ({ ...prev, idPersonaType: value }));
    };

    const updatePersona = async (input: UpdatePersonaCommand) => {
        try {
            setLoading(true);
            
            await invoiceApi.apiVversionPersonaPut("1", input, {});

        } catch (error) {
            console.error('Error updating persona:', error);
        } finally {
            setLoading(false);
        }
    }

    const savePersona = async (input: CreatePersonaCommand) => {
        try {
            setLoading(true);
            await invoiceApi.apiVversionPersonaPost("1", input, {});

        } catch (error) {
            console.error('Error creating persona:', error);
        } finally {
            setLoading(false);
        }
    }

    const handleSubmit = async () => {
        try {
            setErrors({});
            const personaToIds = personaTo.map(entity => entity.id);

            // Validate and submit the form data
            if (mode === 'edit') {
                const parsedData = await personaUpdateSchema(billToDisplay).parseAsync({
                    ...formData,
                    id: editPersona?.id,
                    personaToIds: personaToIds,
                    idCountry: country?.id
                });
                await updatePersona(parsedData);
            } else {
                const parsedData = await personaCreateSchema(billToDisplay).parseAsync({
                    ...formData,
                    idEntity: entityId,
                    idEntityType: idEntityType,
                    personaToIds: personaToIds,
                    idCountry: country?.id
                });
                await savePersona(parsedData);
            }

            onSave && onSave();
        } catch (error) {
            // Handle validation errors
            if (error instanceof z.ZodError) {
                const errorMap: Record<string, string> = {};
                error.errors.forEach(err => {
                    if (err.path) {
                        errorMap[err.path[0].toString()] = err.message;
                    }
                });
                setErrors(errorMap);
            } else {
                console.error('Unexpected error during form submission:', error);
            }
        }
    };

    useEffect(() => {
        if (editPersona) {
            setMode('edit');
            setOpen(true);
            setFormData({
                id: editPersona.id,
                name: editPersona.name,
                email: editPersona.email,
                taxID: editPersona.taxID,
                idPersonaType: editPersona.idPersonaType,
                idCountry: editPersona.idCountry,
                personaToIds: editPersona.invoicingEligibilities.map((entity: InvoicingEligibility) => entity.idPersonaTo),
            });
            setPersonaTo(editPersona.invoicingEligibilities.map((entity: InvoicingEligibility) => ({ id: entity.idPersonaTo, label: entity.personaTo.name })));
            setPersonaTypes(remainingPersonaTypes.concat([
                { id: editPersona.idPersonaType, label: editPersona.personaType.labelKey },
            ]));
            setCountry({ id: editPersona.idCountry, labelKey: editPersona.country.labelKey });
            setErrors({});
        }
    }, [editPersona, open]);

    const newBillingPersonaClick = () => {
        setMode('add');
        setOpen(true);
        // Reset form when opening in add mode
        setFormData({
            id: 0,
            name: '',
            email: '',
            taxID: '',
            countryId: 0,
            personaToIds: [],
            idPersonaType: ''
        });
        if(remainingPersonaTypes?.length === 1) {
            setFormData(prev => ({ ...prev, idPersonaType: remainingPersonaTypes[0].id }));
        }

        if(remainingPersonaTypes?.length === 0) {
            setFormData(prev => ({ ...prev, idPersonaType: editPersona.idPersonaType }));
        }
        setPersonaTypes(remainingPersonaTypes);
        setPersonaTo([]);
        setCountry(undefined);
        setErrors({});
    }

    const closeModal = () => {
        setOpen(false);
        onClose&& onClose();
    };

    return (<>
        {loading &&
            <Spinner small={false} key={"loadingSpinner"}/>
        }
        <PermissionsGate viewScopes={editScopes} editScopes={editScopes} viewRoles={[]} editRoles={[]} >  
            <div className="text-center mt-2">
                <button type="submit" className="btn btn-primary newAddress" disabled={remainingPersonaTypes.length === 0} onClick={newBillingPersonaClick}>
                    <HiOutlinePlus className='mb-1'/> {intl.get('personaCompact.newPersona.button')}
                </button>
            </div>
        </PermissionsGate>
        <Dialog
            open={open}
            fullWidth={true}
            maxWidth="sm"
            onClose={(event: any, reason: string) => {
                if (reason !== "backdropClick") {
                    setOpen(false);
                }
            }}
            PaperProps={{ sx: { overflow: "unset" } }}
        >
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant='h2'>{editPersona && open ? intl.get('persona.title.edit') : intl.get('persona.title.add')}</Typography>
                <IconButton
                    aria-label="close"
                    onClick={closeModal}
                >
                    <MdClose/>
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ overflow: "unset" }}>
                <Stack spacing={2}>
                    <ErrorSummary errors={errors} showEverything={true} mode='warning' ></ErrorSummary>
                    <TextField
                        fullWidth
                        id="name"
                        name="name"
                        label={`${intl.get('personaCompact.table.thead.name')}*`}
                        error={!!errors.name}
                        margin="dense"
                        value={formData.name}
                        onChange={handleChange}
                    />
                    <TextField 
                        name="email"
                        label={`${intl.get('personaCompact.table.thead.email')}`}
                        fullWidth
                        margin="dense"
                        value={formData.email}
                        onChange={handleChange}
                    />
                    <FormControl size='small'error={!!errors.idPersonaType}>
                        <InputLabel id="idPersonaTypeLabel">{`${intl.get('personaCompact.table.thead.type')}*`}</InputLabel>
                        <Select
                            fullWidth
                            labelId='idPersonaTypeLabel'
                            label={`${intl.get('personaCompact.table.thead.type')}*`}
                            id="idPersonaType"
                            name="idPersonaType"
                            error={!!errors.idPersonaType}
                            margin="dense"
                            value={formData.idPersonaType}
                            onChange={handlePersonaTypeChange}
                            disabled={remainingPersonaTypes.length === 0}
                        >
                            {personaTypes.map((option: ListOptionDTO) => (
                                <MenuItem key={option.id} value={option.id}>
                                    {intl.get(option.label)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        fullWidth
                        id="taxID"
                        name="taxID"
                        label={`${intl.get('personaCompact.table.thead.taxId')}*`}
                        error={!!errors.taxID}
                        margin="dense"
                        value={formData.taxID}
                        onChange={handleChange}
                    />
                    <TypeAhead
                        apiPromise={(searchTerm: string) => addressApi.apiVversionAddressCountryTypeaheadTermGet(searchTerm, "1")}
                        getOptionLabel={(option: Country | any) => option.labelKey&&intl.has(option.labelKey)?intl.get(option.labelKey):option.name}
                        value={country}
                        disableClearable={true}
                        label= {intl.get('addressCompact.table.thead.country')}
                        onChange={(event, newValue) => {
                            setCountry(newValue);
                        }}
                    />
                    {billToDisplay &&
                        <>
                            <Typography>{intl.get("personaForm.label.SelectBillTo")}*</Typography>
                            <TypeAhead
                                apiPromise={(searchTerm: string) => invoiceApi.apiVversionListPersonaListGet("1", ENTITY_TYPE.CORPORATE_ENTITY).then(response => { return {data: {data: response?.data?.data?.queryResult.filter((entity: ListOptionDTO) => entity.label.toLowerCase().includes(searchTerm.toLowerCase()))}}})}
                                getOptionLabel={(option: ListOptionDTO | any) => option.label}
                                value={personaTo}
                                disableClearable={true}
                                multiple={true}
                                label= {intl.get('corporateEntitySelect.placeholder.corporateEntity')}
                                onChange={handleCorporateEntitiesChange}
                                error={!!errors.personaToIds}
                                autocompleteProps={
                                    {
                                        renderTags(value, getTagProps, ownerState) {
                                            return value.map((option, index) => ( <Chip key={option.id} label={option.label} color="primary" variant="outlined" {...getTagProps({ index })} />))
                                        },
                                    }
                                }
                            />
                        </>
                    }
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button
                    variant="outlined"
                    color="inherit"
                    onClick={closeModal}
                    className="btn btn-outline-dark"
                >
                    {intl.get('cancel.button')}
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    className="btn btn-primary"
                    disabled={loading}
                    slotProps={{
                        root: {
                            className: "btn btn-primary",
                        },
                    }}
                >
                    <HiOutlineSave className='mb-1'/> {intl.get('save.button')}
                </Button>
            </DialogActions>
        </Dialog>
    </>
    )
}

export default PersonaBillingModal;